import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { combineDateTime } from "@/lib/dates";
import { format } from "date-fns";
import { Repeat } from "lucide-react";

export default function BookingDialog({ open, onClose, onSaved, rooms, initial, allowRecurring = true }) {
  const isEdit = Boolean(initial?.id);
  const [roomId, setRoomId] = useState(initial?.room_id || rooms[0]?.id || "");
  const [date, setDate] = useState(
    initial?.start ? initial.start.slice(0, 10) : format(new Date(), "yyyy-MM-dd"),
  );
  const [startTime, setStartTime] = useState(
    initial?.start ? initial.start.slice(11, 16) : "09:00",
  );
  const [endTime, setEndTime] = useState(
    initial?.end ? initial.end.slice(11, 16) : "10:00",
  );
  const [title, setTitle] = useState(initial?.title || "");
  const [note, setNote] = useState(initial?.note || "");
  const [saving, setSaving] = useState(false);

  // Recurring
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState("weekly");
  const [count, setCount] = useState(4);

  const hasGroup = Boolean(initial?.group_id);

  useEffect(() => {
    // Reset recurring when opening for edit (we don't allow recurring on edit)
    if (isEdit) setIsRecurring(false);
  }, [isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    if (!roomId) {
      toast.error("Vælg et lokale");
      return;
    }
    if (!title.trim()) {
      toast.error("Indtast en titel");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        room_id: roomId,
        title: title.trim(),
        note: note.trim(),
        start: combineDateTime(date, startTime),
        end: combineDateTime(date, endTime),
      };
      if (isEdit) {
        await api.put(`/bookings/${initial.id}`, payload);
        toast.success("Booking opdateret");
      } else {
        if (isRecurring && allowRecurring) {
          payload.recurrence = { frequency, count: parseInt(count) || 2 };
        }
        const res = await api.post("/bookings", payload);
        if (res.data?.created > 1) {
          toast.success(`${res.data.created} bookinger oprettet`);
        } else {
          toast.success("Booking oprettet");
        }
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (series = false) => {
    const msg = series
      ? "Slet hele serien af gentagne bookinger?"
      : "Slet denne booking?";
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/bookings/${initial.id}`, { params: { series } });
      toast.success(series ? "Serie slettet" : "Booking slettet");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[520px] rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? "Rediger booking" : "Ny booking"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Opdater detaljerne for din booking." : "Vælg lokale, dato og tidspunkt for at oprette en ny booking."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Lokale</Label>
            <Select value={roomId} onValueChange={setRoomId}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="booking-room-select">
                <SelectValue placeholder="Vælg lokale" />
              </SelectTrigger>
              <SelectContent>
                {rooms.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ background: r.color }}
                      />
                      {r.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Dato</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-11 rounded-xl"
              data-testid="booking-date-input"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Fra</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="h-11 rounded-xl"
                data-testid="booking-start-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Til</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="h-11 rounded-xl"
                data-testid="booking-end-input"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Titel / Formål</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Fx Salgsmøde"
              className="h-11 rounded-xl"
              data-testid="booking-title-input"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Note (valgfri)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Yderligere info…"
              className="rounded-xl min-h-[80px]"
              data-testid="booking-note-input"
            />
          </div>

          {!isEdit && allowRecurring && (
            <div className="rounded-xl border border-slate-200 p-4 space-y-3 bg-slate-50/50">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 cursor-pointer">
                  <Repeat className="h-4 w-4 text-slate-500" />
                  Gentagende booking
                </Label>
                <Switch
                  checked={isRecurring}
                  onCheckedChange={setIsRecurring}
                  data-testid="recurring-switch"
                />
              </div>
              {isRecurring && (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Frekvens</Label>
                    <Select value={frequency} onValueChange={setFrequency}>
                      <SelectTrigger className="h-10 rounded-lg" data-testid="recurring-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Hver dag</SelectItem>
                        <SelectItem value="weekly">Hver uge</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Antal gentagelser</Label>
                    <Input
                      type="number"
                      min={2}
                      max={52}
                      value={count}
                      onChange={(e) => setCount(e.target.value)}
                      className="h-10 rounded-lg"
                      data-testid="recurring-count"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2 flex-row justify-between sm:justify-between">
            {isEdit ? (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => handleDelete(false)}
                  className="rounded-xl"
                  data-testid="booking-delete-button"
                >
                  Slet
                </Button>
                {hasGroup && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleDelete(true)}
                    className="rounded-xl text-red-600 border-red-200"
                    data-testid="booking-delete-series-button"
                  >
                    Slet hele serien
                  </Button>
                )}
              </div>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="rounded-xl"
                data-testid="booking-cancel-button"
              >
                Annullér
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-slate-900 hover:bg-slate-800"
                data-testid="booking-submit-button"
              >
                {saving ? "Gemmer…" : isEdit ? "Gem ændringer" : "Bekræft booking"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
